import { formatBondDate } from "@/lib/bondValueDisplay";

// BRK-S277 — the ACCOUNT's required steps: the GIA, the surety program and the
// Signal Advance application (design: PMS `docs/design/account-requirements.md`
// §§ 2 and 5.3, epic PMS-E032).
//
// Aaron, SIG-S021: the GIA and the program belong to the ACCOUNT, not to the
// order that triggered them, and they appear on EVERY open bond submission for
// the account and on the account page. PMS derives one read per account and
// publishes it as an optional `requirements` key on the broker order view
// (computed for that order) and on the broker account view.
//
// ── ADOPTED CONSUMER-FIRST, PINNED AT BRK-S280 ──────────────────────────────
//
// Read ahead of the pin until the BRK-S280 re-pin published `requirements` on
// both views; `test/lib/accountRequirementsContract.test.ts` now holds every
// vocabulary below equal to the published enums. Absent still means "PMS has
// not published it" (an older PMS) and renders NOTHING.
//
// ── THE TOLERANT MIRROR (the BRK-S114 lesson) ───────────────────────────────
//
// Every vocabulary below is a CLOSED mirror so each value gets deliberate
// words. But the reader must not break on a value it has not adopted: `state`
// is the one field every consumer can render without knowing the kind, so an
// unknown kind, status, blocker code or action still renders the state chip
// and "Contact your underwriter." An unknown `state` is not actionable at all.
// The wire types are therefore OPEN strings, and `readAccountRequirements`
// does the narrowing.
//
// ── DISCRETION ──────────────────────────────────────────────────────────────
//
// A blocker names a PERSON (their display name) and a plain code — never a
// share, a value or any underwriter vocabulary. No envelope id, no DocuSign
// status word. Nothing here declares a home for one.

/** The kinds, in the order the panel always shows them. */
export const REQUIREMENT_KINDS = ["gia", "program", "signal_advance"] as const;
export type RequirementKind = (typeof REQUIREMENT_KINDS)[number];

/** The one field any consumer can render without knowing the kind. */
export const REQUIREMENT_STATES = [
  "done",
  "action_needed",
  "waiting",
  "optional",
  "not_applicable",
] as const;
export type RequirementState = (typeof REQUIREMENT_STATES)[number];

/** Each kind's `status`, closed per kind (design §§ 2.1–2.3). */
export const REQUIREMENT_STATUSES = {
  gia: [
    "not_applicable",
    "needs_information",
    "not_sent",
    "sent",
    "signed",
    "declined",
    "voided",
  ],
  program: ["approved", "pending", "not_granted", "none"],
  signal_advance: [
    "not_offered",
    "offered",
    "declined",
    "links_pending",
    "in_review",
    // PMS-S722 (2026-10-14): the program was granted, so the row is done.
    // Adopted ahead of PMS emitting it; published since the sprint-10
    // release re-pin.
    "approved",
  ],
} as const satisfies Record<RequirementKind, readonly string[]>;

export type GiaRequirementStatus = (typeof REQUIREMENT_STATUSES.gia)[number];
export type ProgramRequirementStatus =
  (typeof REQUIREMENT_STATUSES.program)[number];
export type AdvanceRequirementStatus =
  (typeof REQUIREMENT_STATUSES.signal_advance)[number];

/** Why a GIA is not done yet — the broker's codes. PMS's admin-only
 *  `template_unavailable` is deliberately absent: it never reaches a broker. */
export const REQUIREMENT_BLOCKER_CODES = [
  "roster_short",
  "marital_status_unknown",
  "spouse_not_recorded",
  "signer_email_missing",
] as const;
export type RequirementBlockerCode = (typeof REQUIREMENT_BLOCKER_CODES)[number];

/** The next actions each kind may offer. */
export const REQUIREMENT_ACTIONS = {
  gia: ["complete_indemnitors", "send_gia"],
  program: ["print_indication", "start_request", "get_preapproved"],
  signal_advance: ["answer_advance", "connect_accounting", "connect_bank"],
} as const satisfies Record<RequirementKind, readonly string[]>;

export type RequirementActionKind =
  (typeof REQUIREMENT_ACTIONS)[RequirementKind][number];

// ── The wire (PMS-S608), open where the reader tolerates ────────────────────

/** `requirements` on the broker order view and the broker account view. */
export interface AccountRequirements {
  accountId: string;
  /** Always gia, program, signal_advance upstream; the reader re-sorts anyway. */
  requirements: Requirement[];
}

/**
 * One required step. `kind`, `state` and `status` are OPEN strings here on
 * purpose: the reader tolerates a value it has not adopted, and a union would
 * claim a narrowing nothing performs.
 */
export interface Requirement {
  kind: string;
  state: string;
  status: string;
  /** Why it is not done, as plain codes. GIA's name people. */
  blocking: RequirementBlocker[];
  nextAction: RequirementAction | null;
  /** Order view only: does this step, as it stands, cover THAT order? */
  coversOrder?: boolean | null;
  /** GIA only — carried over from the order's `gia` block. */
  requiredReason?: string | null;
  /** Program only — which standing program, as the account block names it. */
  programType?: string | null;
  /** Program only — when the standing program runs out (ISO date). */
  expiresAt?: string | null;
}

/**
 * Why a step is not done. The design leaves the field names open; BRK reads
 * the minimal shape: a code and, for a person, their roster id and display
 * name. Never a share or a value.
 */
export interface RequirementBlocker {
  code: string;
  personnelId?: string | null;
  name?: string | null;
}

/** What the broker can do next. `url` rides the two connect actions only. */
export interface RequirementAction {
  kind: string;
  url?: string | null;
}

// ── The read model ───────────────────────────────────────────────────────────

/** A next action this build understands, for a row it understands. */
export interface ReadRequirementAction {
  kind: RequirementActionKind;
  /** The connect link, when the action is a connect action. */
  url: string | null;
}

/** One row, as the panel renders it. */
export interface RequirementRow {
  /** `null` for a kind this build has not adopted. */
  kind: RequirementKind | null;
  /** `null` for a state this build has not adopted — never actionable. */
  state: RequirementState | null;
  /**
   * Whether this build understands the whole row: its kind, its status for
   * that kind, every blocker code, and its action. A row it does not
   * understand renders the state chip and "Contact your underwriter."
   */
  understood: boolean;
  /** The one-line status sentence. */
  sentence: string;
  /** One line per blocker (a person's name where the code names one). */
  blockers: string[];
  /**
   * The first person a blocker names (their `personnelId`), so the GIA's
   * "Complete the owners’ details" lands on them (BRK-S281). Null when no
   * blocker names anyone, or when the row is not understood.
   */
  focusPersonnelId: string | null;
  /** Offered only on an understood row with a known state. */
  action: ReadRequirementAction | null;
  /** The raw status, for the few places that branch on it. */
  status: string;
  coversOrder: boolean | null;
  requiredReason: "first_gia" | "indemnitors_changed" | null;
}

/** What an unknown row says — the tolerant-mirror rule, in one place. */
export const REQUIREMENT_FALLBACK_SENTENCE = "Contact your underwriter.";

function isOneOf<T extends string>(
  list: readonly T[],
  value: unknown,
): value is T {
  return (
    typeof value === "string" && (list as readonly string[]).includes(value)
  );
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

const PROGRAM_NAMES: Record<string, string> = {
  express: "Signal Express",
  advance: "Signal Advance",
};

/** The one-line sentence for a KNOWN kind and status. */
function sentenceFor(
  kind: RequirementKind,
  status: string,
  extras: { programType: string | null; expiresAt: string | null },
): string {
  switch (kind) {
    case "gia":
      switch (status as GiaRequirementStatus) {
        case "not_applicable":
          return "Not needed until a bond request for this insured is approved.";
        case "needs_information":
          return "We need a few details about the owners before it can be sent.";
        case "not_sent":
          return "Ready to send to the owners for signature.";
        case "sent":
          return "Sent — waiting for everyone to sign.";
        case "signed":
          return "Signed. It covers every bond for this insured until the owners change.";
        case "declined":
          return "The last request wasn’t signed. Send a new one to continue.";
        case "voided":
          return "The last request was cancelled. Send a new one to continue.";
      }
      break;
    case "program":
      switch (status as ProgramRequirementStatus) {
        case "approved": {
          const name = extras.programType
            ? PROGRAM_NAMES[extras.programType]
            : undefined;
          const lead = name ? `${name} program approved.` : "Program approved.";
          return extras.expiresAt
            ? `${lead} Expires ${formatBondDate(extras.expiresAt)}.`
            : lead;
        }
        case "pending":
          return "We’re reviewing this insured’s program.";
        case "not_granted":
          return "No program on file. Each bond is reviewed on its own.";
        case "none":
          return "Not pre-approved yet. Get pre-approved to know this insured’s limits before you quote.";
      }
      break;
    case "signal_advance":
      switch (status as AdvanceRequirementStatus) {
        case "not_offered":
          return "Nothing to do here right now.";
        case "offered":
          return "Connect the insured’s bank and accounting to see whether a better rate is available.";
        case "declined":
          return "You passed on this earlier. The offer is still open.";
        case "links_pending":
          return "Your customer still needs to connect their bank and accounting.";
        case "approved":
          return "Signal Advance program approved.";
        case "in_review":
          // Route-neutral: a requirement can be met by documents as well as a link.
          return "Everything’s in — we’re reviewing it and will come back on the rate.";
      }
      break;
  }
  return REQUIREMENT_FALLBACK_SENTENCE;
}

/** One line per blocker code; `null` for a code this build has not adopted. */
function blockerLine(code: string, name: string | null): string | null {
  const who = name ?? "An owner";
  switch (code as RequirementBlockerCode) {
    case "roster_short":
      return "The owner list isn’t complete yet.";
    case "marital_status_unknown":
      return `${who}: marital status isn’t recorded.`;
    case "spouse_not_recorded":
      return `${who}: spouse isn’t recorded.`;
    case "signer_email_missing":
      return `${who}: no email for signing.`;
    default:
      return null;
  }
}

/** Read one wire row. `null` only for something that is not a row at all. */
function readRow(raw: unknown): RequirementRow | null {
  const row = record(raw);
  if (row === null) return null;

  const kind = isOneOf(REQUIREMENT_KINDS, row.kind) ? row.kind : null;
  const state = isOneOf(REQUIREMENT_STATES, row.state) ? row.state : null;
  const status = typeof row.status === "string" ? row.status : "";
  const statusKnown =
    kind !== null && isOneOf(REQUIREMENT_STATUSES[kind], status);

  // Blockers: every code must be known for the row to be understood.
  const rawBlockers = Array.isArray(row.blocking) ? row.blocking : [];
  let blockersKnown = Array.isArray(row.blocking) || row.blocking == null;
  const blockers: string[] = [];
  let focusPersonnelId: string | null = null;
  for (const entry of rawBlockers) {
    const b = record(entry);
    const line =
      b && typeof b.code === "string"
        ? blockerLine(b.code, text(b.name))
        : null;
    if (line === null) blockersKnown = false;
    else if (!blockers.includes(line)) blockers.push(line);
    if (focusPersonnelId === null && line !== null) {
      focusPersonnelId = text(b?.personnelId);
    }
  }

  // The action: null is fine; anything present must be one this kind offers,
  // and a connect action is only an action when it carries a link.
  let action: ReadRequirementAction | null = null;
  let actionKnown = true;
  if (row.nextAction != null) {
    const a = record(row.nextAction);
    const actionKind = a?.kind;
    if (kind !== null && isOneOf(REQUIREMENT_ACTIONS[kind], actionKind)) {
      const url = text(a?.url);
      const needsUrl =
        actionKind === "connect_accounting" || actionKind === "connect_bank";
      if (needsUrl && url === null) actionKnown = false;
      else action = { kind: actionKind, url: needsUrl ? url : null };
    } else {
      actionKnown = false;
    }
  }

  const understood = statusKnown && blockersKnown && actionKnown;
  const requiredReason =
    row.requiredReason === "first_gia" ||
    row.requiredReason === "indemnitors_changed"
      ? row.requiredReason
      : null;

  return {
    kind,
    state,
    understood,
    sentence:
      understood && kind !== null
        ? sentenceFor(kind, status, {
            programType: text(row.programType),
            expiresAt: text(row.expiresAt),
          })
        : REQUIREMENT_FALLBACK_SENTENCE,
    blockers: understood ? blockers : [],
    focusPersonnelId: understood ? focusPersonnelId : null,
    // An unknown state is never actionable; neither is a row not understood.
    action: understood && state !== null ? action : null,
    status,
    coversOrder: typeof row.coversOrder === "boolean" ? row.coversOrder : null,
    requiredReason,
  };
}

/**
 * The `requirements` key, read into panel rows — or `null` when PMS has not
 * published it (absent, `null`, or not the published shape), which renders
 * NOTHING.
 *
 * Rows come back in the fixed order GIA, program, Signal Advance, then any
 * kind this build has not adopted in the order PMS sent them. A second row of
 * a kind already read is dropped: the design publishes one per kind.
 */
export function readAccountRequirements(raw: unknown): RequirementRow[] | null {
  const block = record(raw);
  if (block === null || !Array.isArray(block.requirements)) return null;
  const known = new Map<RequirementKind, RequirementRow>();
  const unknown: RequirementRow[] = [];
  for (const entry of block.requirements) {
    const row = readRow(entry);
    if (row === null) continue;
    if (row.kind === null) unknown.push(row);
    else if (!known.has(row.kind)) known.set(row.kind, row);
  }
  const ordered = REQUIREMENT_KINDS.flatMap((k) => {
    const row = known.get(k);
    return row ? [row] : [];
  });
  return [...ordered, ...unknown];
}
